import React, { useState, useContext, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { Button, Modal, Breadcrumb } from 'react-bootstrap';
import axios from 'axios';
import { ProductContext } from '../../ProductProvider';
import logo from '../../assets/images/logo/COLLEGELOGO.jpeg';
import ImageDisplay from './ImageDisplay';
import Preloader from '../../components/Preloader/Preloader';
import '../../styles.css';
import { Helmet } from 'react-helmet-async';
import { Link } from "react-router-dom";
import { fetchAuthSession } from 'aws-amplify/auth';
import AddCompressSlider from './AddCompressSlider';

const config = require('../../config.json');

export default function SliderViewer() {
    const { validateLogin, loggedUser } = useContext(ProductContext);
    const [loaded, setLoaded] = useState(false);
    const [galleryImages, setGalleryImages] = useState([]);
    const [open, setOpen] = useState(false);
    const [imageFile, setImageFile] = useState('');
    const [profileImage, setProfileImage] = useState(logo);
    const [newFileName, setNewFileName] = useState('');
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        if (loggedUser.isAuthenticated && loggedUser.profile === "Super Student") {
          fetchGalleryImages();
        } else {
          validateLogin();
        }
    }, [loggedUser]);

    const fetchGalleryImages = async () => {
      try {
            setLoaded(false);
            const fileName = 'images/slider';
            const bucketName = "images.vidyajyoticollege.in";
            await axios.get(`${config.api.invokeURL}/gallery/slider?fileName=${fileName}&bucketName=${bucketName}`, {
              headers: {
                'x-api-key': config.api.key
              }
            }).then((response) => {
              const finalList = [];
              if (response.data && response.data.length > 0) {
                for (const i in response.data) {
                  const tValue = response.data[i];
                  const tPathName = tValue.split("/");
                  const tBreakUp = tPathName[2];
                  const tempValue = {};
                  tempValue.fullPathName = response.data[i];
                  tempValue.fileName = tBreakUp;
                  finalList.push(tempValue);
                }
              }
              setGalleryImages(finalList);
              enqueueSnackbar(finalList.length + " - Slider images found", { variant: 'success' });
              setLoaded(true);
            }).catch((error) => {
              enqueueSnackbar("Error: " + error.message, { variant: 'warning' }); 
              setLoaded(true);
            });
      } catch (error) {
         enqueueSnackbar("Error: " + error.message, { variant: 'warning' });
         setLoaded(true);
      }
    };

    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const postOnS3 = async (event) => {
        event.preventDefault();
        try { 
            if (!imageFile || !newFileName.trim()) {
                enqueueSnackbar("Please enter all details and select an image", { variant: 'info' });
                return;
            }
            setLoaded(false);
            const fileName = newFileName.trim();
            const tnewFileName = fileName + ".jpeg";
            const keyName = '/images/slider';
            const bucketName = config.s3.bucketName + keyName;
            const file = imageFile;
            const blob = file.slice(0, file.size, 'image/jpeg'); 
            const newFile = new File([blob], tnewFileName, { type: 'image/jpeg' });
            const { idToken } = (await fetchAuthSession()).tokens ?? {};

            await axios.get(`${config.api.invokeURL}/presigned?fileName=${tnewFileName}&bucketName=${bucketName}`, {
                headers: {
                   Authorization: idToken,
                   'x-api-key': config.api.key
                }
            }).then(async (response) => {
                const res = await fetch(response.data.uploadURL, {
                  method: 'PUT',
                  headers: {
                    "Content-Type": "image/jpeg"
                  },
                  body: newFile
                });
                if (res.status === 200) {
                  enqueueSnackbar("Slider image uploaded successfully!", { variant: 'success' });
                  fetchGalleryImages();
                  setProfileImage(newFileName);
                  handleClose();
                }
            }).catch((error) => {
                enqueueSnackbar("Error: " + error.message, { variant: 'warning' });
                setLoaded(true);
            });
        } catch (error) {
            enqueueSnackbar("Error: " + error.message, { variant: 'warning' }); 
            setLoaded(true);
        }
    };

    const handleDeleteImage = async (event, imageName) => {
        event.preventDefault();
        try {
            if (galleryImages.length < 2) {
              enqueueSnackbar("Delete not allowed! At least one image is required in Slider", { variant: 'info' });
              return;
            }
            setLoaded(false);
            const { idToken } = (await fetchAuthSession()).tokens ?? {};
            await axios.get(`${config.api.invokeURL}/gallery/remove?imageName=${imageName}`, {
              headers: {
                  Authorization: idToken,
                  'x-api-key': config.api.key
              }
            }).then(() => {
              enqueueSnackbar("Image deleted successfully", { variant: 'success' });
              fetchGalleryImages();
            }).catch((error) => {
              enqueueSnackbar("Error: " + error.message, { variant: 'warning' });
              setLoaded(true);
            });
        } catch (error) {
          enqueueSnackbar("Error: " + error.message, { variant: 'warning' });
          setLoaded(true);
        }
    };
  
    return (
      <div className="slider-viewer-page animate-fade-in-up">
        <Preloader show={!loaded} />
        <Helmet>
          <title>Website Slider Management - Vidyajyoti College</title>
          <meta name="description" content="Manage homepage carousel images" />
          <link rel="canonical" href="/slider-viewer" />
        </Helmet>

        {/* Breadcrumb */}
        <Breadcrumb>
          <Breadcrumb.Item>
            <Link to="/">
              <i className="fa fa-home" aria-hidden="true"></i> Dashboard
            </Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>Websites</Breadcrumb.Item>
          <Breadcrumb.Item active>Slider</Breadcrumb.Item>
        </Breadcrumb>

        <ImageDisplay 
          galleryImages={galleryImages} 
          handleDeleteImage={handleDeleteImage} 
          postOnS3={postOnS3} 
          titleName="Homepage Slider Images" 
          subTitleName="Live carousel banner images on the college portal"
          handleOpen={handleOpen}
        />       

        {/* Add Modal */}
        <Modal show={open} onHide={handleClose} size="md" centered>
          <Modal.Header closeButton>
            <Modal.Title className="fw-bold">
              <i className="fa fa-plus-circle text-primary me-2"></i> Add Slider Image
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <AddCompressSlider 
              newFileName={newFileName} 
              setNewFileName={setNewFileName} 
              setImageFile={setImageFile} 
              profileImage={profileImage} 
            />
          </Modal.Body>
          <Modal.Footer className="d-flex justify-content-end gap-2">
            <Button variant="light" onClick={handleClose} style={{ borderRadius: '10px' }}>
              Cancel
            </Button>
            <Button variant="primary" onClick={postOnS3} style={{ borderRadius: '10px' }}>
              <i className="fa fa-upload me-1"></i> Upload Image
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
}